/**
 * SettingsAppearanceSection — persisted Appearance controls for the
 * Abyss visual identity. Currently exposes a single dial: Caustic
 * Intensity, which adjusts the --caustic-intensity CSS variable live
 * without requiring a code change or page reload.
 */
import { useEffect, useState } from 'react';
import { Card, CardBody, CardHeader, CardTitle } from '../../components/Card';
import { userDataApi } from '../../lib/userdata-api';

/** Valid range for the caustic intensity control. */
const MIN_INTENSITY = 0;
const MAX_INTENSITY = 0.15;
const DEFAULT_INTENSITY = 0.04;

/** Shape of the `appearance` settings namespace. */
interface AppearanceSettings {
  causticIntensity: number;
}

/** Narrow unknown → AppearanceSettings safely. */
function narrowAppearance(raw: unknown): AppearanceSettings {
  if (raw !== null && typeof raw === 'object' && !Array.isArray(raw)) {
    const obj = raw as Record<string, unknown>;
    const causticIntensity =
      typeof obj.causticIntensity === 'number'
        ? obj.causticIntensity
        : DEFAULT_INTENSITY;
    return { causticIntensity };
  }
  return { causticIntensity: DEFAULT_INTENSITY };
}

export function SettingsAppearanceSection() {
  const [intensity, setIntensity] = useState<number>(DEFAULT_INTENSITY);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Load persisted value on mount.
  useEffect(() => {
    void (async () => {
      const result = await userDataApi.get('appearance');
      if (!result.ok) return;
      const { causticIntensity } = narrowAppearance(result.value.data);
      setIntensity(causticIntensity);
    })();
  }, []);

  /** Apply the value live to the CSS variable and persist it. */
  async function handleChange(next: number) {
    setIntensity(next);
    setSaveError(null);
    // Apply immediately so the overlay updates behind the settings panel.
    document.documentElement.style.setProperty(
      '--caustic-intensity',
      String(next),
    );
    setSaving(true);
    const result = await userDataApi.put('appearance', {
      causticIntensity: next,
    } satisfies AppearanceSettings);
    setSaving(false);
    if (!result.ok) {
      setSaveError(result.error);
    }
  }

  const pct = Math.round(
    ((intensity - MIN_INTENSITY) / (MAX_INTENSITY - MIN_INTENSITY)) * 100,
  );

  return (
    <Card className="mt-6" data-testid="settings-appearance-section">
      <CardHeader>
        <CardTitle>Appearance</CardTitle>
      </CardHeader>
      <CardBody>
        <p className="text-xs text-slate-400 mb-4">
          Adjust the underwater caustic shimmer behind the app. Set to 0 to
          disable it entirely.
        </p>

        <label
          className="flex flex-col gap-2"
          htmlFor="caustic-intensity-slider"
        >
          <span className="text-sm font-medium text-slate-200">
            Caustic intensity
            <span
              className="ml-2 num text-slate-400 text-xs tabular-nums"
              aria-label={`Current value: ${pct}%`}
              data-testid="caustic-intensity-display"
            >
              {pct}%
            </span>
            {saving ? (
              <span className="ml-2 text-xs text-slate-500">Saving…</span>
            ) : null}
          </span>
          <input
            id="caustic-intensity-slider"
            type="range"
            min={MIN_INTENSITY}
            max={MAX_INTENSITY}
            step={0.005}
            value={intensity}
            onChange={(e) => {
              void handleChange(parseFloat(e.target.value));
            }}
            className="w-full accent-[color:var(--accent)] cursor-pointer"
            aria-label="Caustic overlay intensity"
            data-testid="caustic-intensity-slider"
          />
          <div className="flex justify-between text-[10px] text-slate-500 select-none">
            <span>Off</span>
            <span>Subtle (default 4%)</span>
            <span>Strong (15%)</span>
          </div>
        </label>

        {saveError !== null ? (
          <p
            className="mt-2 text-xs text-[color:var(--bad)]"
            role="alert"
            data-testid="caustic-save-error"
          >
            Could not save: {saveError}
          </p>
        ) : null}
      </CardBody>
    </Card>
  );
}
